if (!customElements.get('journey-video-section')) {
  class JourneyVideoSection extends HTMLElement {
    connectedCallback() {
      if (this.journeyVideoReady) return;
      this.journeyVideoReady = true;

      this.cards = Array.from(this.querySelectorAll('[data-journey-card]'));
      this.panels = Array.from(this.querySelectorAll('[data-episode-panel]'));
      this.panelsContainer = this.querySelector('[data-episode-panels]');
      this.availableGroups = new Set(this.panels.map((panel) => panel.dataset.group));
      this.desktopEpisodeLayout = window.matchMedia('(min-width: 769px)');
      this.descriptions = Array.from(this.querySelectorAll('[data-episode-description]'));
      this.scheduleDescriptionFit = this.scheduleDescriptionFit.bind(this);

      this.cards.forEach((card) => this.bindCard(card));
      this.addEventListener('modalClosed', (event) => this.handleModalClosed(event));
      this.addEventListener('shopify:block:select', (event) => this.handleBlockSelect(event));
      this.setupDescriptionFit();

      const initialGroup = this.dataset.activeGroup;
      if (initialGroup && this.canSelect(initialGroup)) {
        this.selectGroup(initialGroup);
      } else {
        this.hidePanels();
      }
    }

    disconnectedCallback() {
      this.descriptionResizeObserver?.disconnect();
      this.desktopEpisodeLayout?.removeEventListener?.('change', this.scheduleDescriptionFit);
      if (this.descriptionFitFrame) cancelAnimationFrame(this.descriptionFitFrame);
    }

    setupDescriptionFit() {
      if (!this.descriptions.length) return;

      this.descriptionResizeObserver = new ResizeObserver(this.scheduleDescriptionFit);
      this.descriptions.forEach((description) => {
        const card = description.closest('.journey-video__episode-card');
        const media = card?.querySelector('.journey-video__episode-media');
        const info = description.closest('.journey-video__episode-info');

        if (media) this.descriptionResizeObserver.observe(media);
        if (info) this.descriptionResizeObserver.observe(info);
      });

      this.desktopEpisodeLayout.addEventListener?.('change', this.scheduleDescriptionFit);
      this.scheduleDescriptionFit();
    }

    scheduleDescriptionFit() {
      if (this.descriptionFitFrame) cancelAnimationFrame(this.descriptionFitFrame);
      this.descriptionFitFrame = requestAnimationFrame(() => {
        this.descriptionFitFrame = null;
        this.fitDescriptions();
      });
    }

    fitDescriptions() {
      const useCoverHeight = this.desktopEpisodeLayout.matches;

      this.descriptions.forEach((description) => {
        description.style.removeProperty('--journey-video-description-lines');
        if (!useCoverHeight) return;

        const info = description.closest('.journey-video__episode-info');
        const copy = description.closest('.journey-video__episode-copy');
        const title = copy?.querySelector('.journey-video__episode-title');
        if (!info || !copy || info.clientHeight === 0) return;

        const infoStyles = getComputedStyle(info);
        const innerHeight = info.clientHeight
          - parseFloat(infoStyles.paddingTop)
          - parseFloat(infoStyles.paddingBottom);
        let availableHeight = innerHeight;

        if (title) {
          const titleStyles = getComputedStyle(title);
          availableHeight -= title.getBoundingClientRect().height
            + parseFloat(titleStyles.marginTop)
            + parseFloat(titleStyles.marginBottom);
        }

        const descriptionStyles = getComputedStyle(description);
        const lineHeight = parseFloat(descriptionStyles.lineHeight);
        if (!Number.isFinite(lineHeight) || lineHeight <= 0) return;

        const lineCount = Math.max(1, Math.floor((availableHeight + 0.5) / lineHeight));
        description.style.setProperty('--journey-video-description-lines', String(lineCount));
      });
    }

    bindCard(card) {
      const selector = card.querySelector('[data-journey-selector]');
      if (!selector) return;

      selector.addEventListener('click', () => this.selectGroup(card.dataset.group));
      card.addEventListener('mouseenter', () => {
        const desktopHover = window.matchMedia('(min-width: 769px) and (hover: hover) and (pointer: fine)').matches;
        if (desktopHover) this.selectGroup(card.dataset.group);
      });
    }

    canSelect(group) {
      return this.cards.some((card) => {
        return card.dataset.group === group
          && card.dataset.unlocked === 'true'
          && card.dataset.selectable === 'true'
          && this.availableGroups.has(group);
      });
    }

    selectGroup(group) {
      if (!this.canSelect(group)) return;

      this.dataset.activeGroup = group;
      this.cards.forEach((card) => {
        const active = card.dataset.group === group
          && card.dataset.unlocked === 'true'
          && card.dataset.selectable === 'true';
        const cardElement = card.querySelector('.journey-video__journey-card');
        const selector = card.querySelector('[data-journey-selector]');

        cardElement?.classList.toggle('is-active', active);
        selector?.setAttribute('aria-pressed', String(active));
      });

      let activePanel = null;
      this.panels.forEach((panel) => {
        const active = panel.dataset.group === group;
        panel.hidden = !active;
        if (active) activePanel = panel;
      });

      if (this.panelsContainer) this.panelsContainer.hidden = !activePanel;
      this.scheduleDescriptionFit();
    }

    hidePanels() {
      this.cards.forEach((card) => {
        card.querySelector('.journey-video__journey-card')?.classList.remove('is-active');
        card.querySelector('[data-journey-selector]')?.setAttribute('aria-pressed', 'false');
      });
      this.panels.forEach((panel) => {
        panel.hidden = true;
      });
      if (this.panelsContainer) this.panelsContainer.hidden = true;
    }

    handleModalClosed(event) {
      const modal = event.target.closest('modal-dialog');
      if (!modal || !this.contains(modal)) return;

      modal.querySelector('.modal__content')?.replaceChildren();
      const deferredMedia = this.querySelector(`deferred-media[data-modal="${modal.id}"]`);
      deferredMedia?.removeAttribute('loaded');
    }

    handleBlockSelect(event) {
      const card = event.target.closest('[data-journey-card]');
      if (card) {
        this.selectGroup(card.dataset.group);
        return;
      }

      const episode = event.target.closest('.journey-video__episode-item');
      const panel = episode?.closest('[data-episode-panel]');
      if (panel) this.selectGroup(panel.dataset.group);
    }
  }

  customElements.define('journey-video-section', JourneyVideoSection);
}
